import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  Archive,
  Building2,
  Loader2,
  MessageSquarePlus,
  RotateCcw,
  Search,
  Send,
  UserRound,
  X,
} from "lucide-react";
import { useMessaging } from "../hooks/useMessaging";
import { AuthContext } from "../../../../context/AuthContext";
import { searchPublicEntities } from "../../../../api/auth";
import { createConversation } from "../../../../api/marketplace";
import { usePublicEntities } from "../../../../hooks/usePublicEntities";

const TYPE_LABELS = { AGENCY_OWNER: "Agence", AGENCY_AGENT: "Agent", STORE: "Store" };

function Avatar({ profile, type, size = "h-10 w-10" }) {
  const Icon = type === "AGENCY_OWNER" ? Building2 : UserRound;
  if (profile?.avatar) {
    return <img src={profile.avatar} alt="" className={`${size} shrink-0 rounded-full object-cover`} />;
  }
  return (
    <span className={`${size} flex shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-600`}>
      <Icon className="h-4 w-4" />
    </span>
  );
}

function StatusPill({ status }) {
  const styles = {
    active: "bg-emerald-50 text-emerald-700",
    archived: "bg-slate-100 text-slate-600",
    blocked: "bg-rose-50 text-rose-700",
  };
  const labels = { active: "Active", archived: "Archivée", blocked: "Bloquée" };
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${styles[status] || styles.active}`}>
      {labels[status] || status}
    </span>
  );
}

/* ---------- Nouvelle conversation : recherche par nom ---------- */
function NewConversationModal({ open, onClose, onCreated }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setSelected(null);
      setSubject("");
      setMessage("");
      setError("");
    }
  }, [open]);

  useEffect(() => {
    if (!open || selected) return undefined;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return undefined;
    }
    let alive = true;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await searchPublicEntities(q);
        if (alive) setResults(res.data?.results || []);
      } catch {
        if (alive) setResults([]);
      } finally {
        if (alive) setSearching(false);
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query, open, selected]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSending(true);
    setError("");
    try {
      const res = await createConversation({
        receiver_type: selected.entity_type,
        receiver_id: selected.entity_id,
        subject: subject.trim() || `Discussion avec ${selected.display_name}`,
        initial_message: message.trim() || undefined,
      });
      onCreated(res.data);
    } catch (err) {
      const data = err?.response?.data;
      setError(data?.detail || (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]) || "Impossible de créer la conversation.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="modal-surface w-full max-w-lg rounded-2xl bg-white shadow-xl dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Nouvelle conversation</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Fermer">
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4 p-5">
          {!selected ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Agent ou agence de confirmation
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Tapez un nom (2 lettres minimum)…"
                  className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-9 text-sm dark:border-slate-700"
                />
                {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />}
              </div>

              <div className="mt-2 max-h-72 overflow-y-auto">
                {query.trim().length >= 2 && !searching && results.length === 0 ? (
                  <p className="empty-state rounded-lg bg-slate-50 p-4 text-center text-sm text-slate-500">Aucun agent ni agence trouvé.</p>
                ) : (
                  results.map((r) => (
                    <button
                      key={`${r.entity_type}:${r.entity_id}`}
                      type="button"
                      onClick={() => {
                        setSelected(r);
                        setSubject(`Discussion avec ${r.display_name}`);
                      }}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      <Avatar profile={r} type={r.entity_type} size="h-9 w-9" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{r.display_name}</span>
                        <span className="block truncate text-xs text-slate-500">{r.subtitle}</span>
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                        {TYPE_LABELS[r.entity_type] || r.entity_type}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3 dark:bg-slate-800">
                <Avatar profile={selected} type={selected.entity_type} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{selected.display_name}</div>
                  <div className="truncate text-xs text-slate-500">{TYPE_LABELS[selected.entity_type]} · {selected.subtitle}</div>
                </div>
                <button type="button" onClick={() => setSelected(null)} className="text-xs font-semibold text-indigo-600">
                  Changer
                </button>
              </div>

              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Sujet</span>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm dark:border-slate-700"
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Message</span>
                <textarea
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Bonjour, …"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm dark:border-slate-700"
                />
              </label>
            </>
          )}

          {error && <p className="text-sm text-rose-600">{error}</p>}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-outline">
              Annuler
            </button>
            <button type="submit" disabled={!selected || sending} className="btn-primary disabled:opacity-60">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Envoyer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function AdminMarketplaceMessaging() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    conversations,
    messages,
    stats,
    loading,
    error,
    fetchConversations,
    fetchMessages,
    fetchStats,
    fetchUnread,
    send,
    markRead,
    archive,
    reopen,
  } = useMessaging();
  const [selectedId, setSelectedId] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [search, setSearch] = useState("");
  const [newModal, setNewModal] = useState(false);
  const [openingChat, setOpeningChat] = useState(false);
  const [chatError, setChatError] = useState("");
  const messagesEndRef = useRef(null);

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.id === selectedId) || null,
    [conversations, selectedId]
  );

  const participants = useMemo(
    () => conversations.map((c) => ({ type: c.other_participant?.type, id: c.other_participant?.id })),
    [conversations]
  );
  const { getEntity } = usePublicEntities(participants);
  const nameOf = useCallback(
    (conv) => {
      const other = conv?.other_participant;
      const profile = getEntity(other?.type, other?.id);
      return profile?.display_name || TYPE_LABELS[other?.type] || "Interlocuteur";
    },
    [getEntity]
  );

  useEffect(() => {
    fetchConversations();
    fetchStats();
    fetchUnread();
  }, [fetchConversations, fetchStats, fetchUnread]);

  // Ouverture directe depuis une offre / une collaboration (?receiver_type=&receiver_id=...)
  const receiverType = searchParams.get("receiver_type");
  const receiverId = searchParams.get("receiver_id");
  useEffect(() => {
    if (!receiverType || !receiverId) return;
    let alive = true;
    (async () => {
      setOpeningChat(true);
      setChatError("");
      try {
        const payload = {
          receiver_type: receiverType,
          receiver_id: receiverId,
          subject: searchParams.get("subject") || undefined,
        };
        if (searchParams.get("collaboration_id")) payload.collaboration = searchParams.get("collaboration_id");
        if (searchParams.get("offer_id")) payload.offer = searchParams.get("offer_id");
        const res = await createConversation(payload);
        if (!alive) return;
        await fetchConversations();
        setSelectedId(res.data?.id || null);
      } catch (err) {
        if (alive) setChatError(err?.response?.data?.detail || "Impossible d'ouvrir la conversation.");
      } finally {
        if (alive) {
          setOpeningChat(false);
          navigate("/admin-marketplace/messaging", { replace: true });
        }
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiverType, receiverId]);

  useEffect(() => {
    if (selectedId) {
      fetchMessages(selectedId);
      markRead(selectedId);
    }
  }, [fetchMessages, markRead, selectedId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    await send(selectedConversation.id, newMessage);
    setNewMessage("");
  };

  const filtered = conversations.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [c.subject, c.last_message_preview, nameOf(c)].some((v) => String(v || "").toLowerCase().includes(q));
  });

  if (loading && !conversations.length && !openingChat) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error && !conversations.length) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-center">
        <AlertCircle size={32} className="mx-auto mb-2 text-rose-500" />
        <p className="font-medium text-rose-700">Impossible de charger les messages</p>
        <button onClick={() => fetchConversations()} className="btn-primary mt-3">
          Réessayer
        </button>
      </div>
    );
  }

  const otherProfile = selectedConversation
    ? getEntity(selectedConversation.other_participant?.type, selectedConversation.other_participant?.id)
    : null;

  return (
    <div className="page-shell space-y-5">
      <div className="page-header-card flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Marketplace</div>
          <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">Messagerie</h1>
          <p className="mt-1 text-sm text-slate-500">Échangez avec vos agents et agences de confirmation.</p>
        </div>
        <button onClick={() => setNewModal(true)} className="btn-primary">
          <MessageSquarePlus className="h-4 w-4" />
          Nouvelle conversation
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            ["Conversations", stats.conversation_count, "text-slate-900 dark:text-slate-100"],
            ["Actives", stats.active_count, "text-emerald-600"],
            ["Messages non lus", stats.unread_messages, "text-amber-600"],
          ].map(([label, value, tone]) => (
            <div key={label} className="metric-card rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
              <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">{label}</p>
              <p className={`mt-1 text-2xl font-bold ${tone}`}>{value ?? 0}</p>
            </div>
          ))}
        </div>
      )}

      {(openingChat || chatError) && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${chatError ? "border-rose-200 bg-rose-50 text-rose-700" : "border-indigo-200 bg-indigo-50 text-indigo-700"}`}>
          {chatError || "Ouverture de la conversation…"}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        {/* Liste */}
        <div className="surface-card overflow-hidden rounded-xl border border-slate-200 bg-white p-0 dark:border-slate-800 dark:bg-slate-900 xl:col-span-1">
          <div className="border-b border-slate-100 p-3 dark:border-slate-800">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher (nom, sujet, message)…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm dark:border-slate-700"
              />
            </div>
          </div>
          <div className="max-h-[620px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">
                Aucune conversation.{" "}
                <button onClick={() => setNewModal(true)} className="font-semibold text-indigo-600">
                  En démarrer une
                </button>
              </div>
            ) : (
              filtered.map((conv) => {
                const active = selectedId === conv.id;
                const profile = getEntity(conv.other_participant?.type, conv.other_participant?.id);
                return (
                  <button
                    key={conv.id}
                    onClick={() => setSelectedId(conv.id)}
                    className={`flex w-full items-start gap-3 border-b border-slate-100 p-3 text-left transition hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800 ${
                      active ? "bg-indigo-50" : ""
                    }`}
                  >
                    <Avatar profile={profile} type={conv.other_participant?.type} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{nameOf(conv)}</span>
                        <span className="shrink-0 text-[10px] text-slate-400">
                          {conv.last_message_at ? new Date(conv.last_message_at).toLocaleDateString("fr-FR") : ""}
                        </span>
                      </span>
                      <span className="block truncate text-xs font-medium text-slate-600 dark:text-slate-300">{conv.subject}</span>
                      <span className="mt-0.5 flex items-center justify-between gap-2">
                        <span className="truncate text-xs text-slate-500">{conv.last_message_preview || "—"}</span>
                        {conv.unread_count > 0 && (
                          <span className="shrink-0 rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-semibold text-white">{conv.unread_count}</span>
                        )}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Fil de messages */}
        <div className="teqa-thread flex flex-col rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 xl:col-span-2" style={{ minHeight: 620 }}>
          {selectedConversation ? (
            <>
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 p-4 dark:border-slate-800">
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar profile={otherProfile} type={selectedConversation.other_participant?.type} />
                  <div className="min-w-0">
                    <h3 className="truncate font-semibold text-slate-900 dark:text-slate-100">{nameOf(selectedConversation)}</h3>
                    <p className="truncate text-xs text-slate-500">
                      {selectedConversation.subject}
                      {selectedConversation.collaboration_details?.offer_details?.titre
                        ? ` · Collaboration : ${selectedConversation.collaboration_details.offer_details.titre}`
                        : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status={selectedConversation.status} />
                  {selectedConversation.status === "active" ? (
                    <button onClick={() => archive(selectedConversation.id)} className="icon-button !h-9 !w-9 rounded-lg" title="Archiver">
                      <Archive size={16} />
                    </button>
                  ) : (
                    <button onClick={() => reopen(selectedConversation.id)} className="icon-button !h-9 !w-9 rounded-lg" title="Rouvrir">
                      <RotateCcw size={16} />
                    </button>
                  )}
                </div>
              </div>

              <div className="teqa-thread__messages flex-1 space-y-3 overflow-y-auto p-4">
                {messages.map((msg) => {
                  const isMine = user?.store?.id && String(msg.sender_id) === String(user.store.id);
                  return (
                    <div key={msg.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                      <div className={`message-bubble max-w-md ${isMine ? "sent" : "received"}`}>
                        <p className="whitespace-pre-wrap">{msg.body}</p>
                        <p className={`mt-1 text-[10px] ${isMine ? "text-white/70" : "text-slate-400"}`}>
                          {new Date(msg.created_at).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {selectedConversation.status === "active" && (
                <div className="border-t border-slate-100 p-3 dark:border-slate-800">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSend()}
                      placeholder="Écrire un message…"
                      className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 text-sm dark:border-slate-700"
                    />
                    <button onClick={handleSend} disabled={!newMessage.trim()} className="btn-primary disabled:opacity-50" aria-label="Envoyer">
                      <Send size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <MessageSquarePlus className="h-10 w-10 text-slate-300" />
              <p className="text-sm text-slate-500">Choisissez une conversation ou démarrez-en une nouvelle.</p>
              <button onClick={() => setNewModal(true)} className="btn-outline">
                Chercher un agent ou une agence
              </button>
            </div>
          )}
        </div>
      </div>

      <NewConversationModal
        open={newModal}
        onClose={() => setNewModal(false)}
        onCreated={async (conversation) => {
          setNewModal(false);
          await fetchConversations();
          setSelectedId(conversation?.id || null);
        }}
      />
    </div>
  );
}
